import Big from 'big.js';
import {combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, startWith} from 'rxjs/operators';
import {calcValueRv} from '@/calc';
import {gte, numUtil} from './num';
import type {LeverageEntity, RiskLimit, ContractPair, Leverage, Position, LeverageMargins} from '@/types';

export const LeverageDefault = {
  unit: 'X',
  precision: 2,
};

export const leverageUtil = {
  marginRateErToLeverageEr(marginRateEr: number, marginRateScale: number = 8, leverageScale: number = 8) {
    /**
     * 杠杆: leverageEr = (1 * MarginRateFactor / marginRateEr) * leverageFactor
     *
     * MarginRateFactor = contract.ratioScale
     * LeverageFactor = contract.ratioScale
     */
    const leverageEr = Big(10)
      .pow(marginRateScale + leverageScale)
      .div(marginRateEr)
      .toNumber();
    return Number(Big(leverageEr).round(2, Big.roundUp).toFixed(2));
  },
  marginRateErToLeverage(marginRateEr: number, marginRateScale: number = 8, leverageScale: number = 8) {
    return this.leverageErToLeverage(
      this.marginRateErToLeverageEr(marginRateEr, marginRateScale, leverageScale),
      leverageScale
    );
  },
  leverageErToLeverage(
    leverageEr: number,
    leverageScale: number = 8,
    {removeEnd0 = false, unit = LeverageDefault.unit}: {removeEnd0?: boolean; unit?: string} = {}
  ): string {
    const leverage = numUtil.ceil(leverageEr, LeverageDefault.precision, leverageScale);
    return (removeEnd0 ? numUtil.trimEnd0(leverage) : leverage) + unit;
  },
  createMaxLeverage$(riskLimit$: Observable<RiskLimit>, contract$: Observable<ContractPair>) {
    const initialMarginEr$ = createInitialMarginEr$(riskLimit$);
    return combineLatest([initialMarginEr$, contract$]).pipe(
      map(([initialMarginEr, contract]) => convertMaxLeverage(initialMarginEr, contract.ratioScale)),
      filter(Boolean),
      startWith({leverageEr: 1e8, leverageX: '1X'}),
      shareReplay<LeverageEntity>(1)
    );
  },
  createMaxLeverage(riskLimit: RiskLimit, contract: ContractPair): LeverageEntity {
    if (!riskLimit || !contract) {
      return {leverageEr: 1e8, leverageX: '1X'};
    }
    return convertMaxLeverage(riskLimit.initialMarginEr, contract.ratioScale);
  },
  createLeverageOptions$(leverages$: Observable<Leverage[]>, riskLimit$: Observable<RiskLimit>) {
    const initialMarginEr$ = createInitialMarginEr$(riskLimit$);
    return combineLatest([initialMarginEr$, leverages$]).pipe(
      map(([initialMarginEr, leverages]) => convertLeverageOptions(initialMarginEr, leverages)),
      filter(Boolean),
      startWith([]),
      distinctUntilChanged(),
      shareReplay<LeverageEntity[]>(1)
    );
  },
  createLeverageOptions(leverages: Leverage[], riskLimit: RiskLimit): LeverageEntity[] {
    if (!leverages || !riskLimit) {
      return [];
    }
    return convertLeverageOptions(riskLimit.initialMarginEr, leverages);
  },
  getMaxLeverageByLeverageOptions(leverageOptions: LeverageEntity[]): LeverageEntity {
    const maxLeverage = leverageOptions.reduce((p, c) => (Big(p.leverageEr).gt(c.leverageEr) ? p : c), {leverageEr: 0});
    return {
      ...maxLeverage,
      leverageX: `${maxLeverage.leverage}X`,
    };
  },
  getMaxLeverageByLeverages(leverages: Leverage[], riskLimit: RiskLimit): number {
    return this.getMaxLeverageByLeverageOptions(this.createLeverageOptions(leverages, riskLimit));
  },
  convertSlideOptions(options: LeverageEntity[]) {
    return options
      .sort((a, b) => a.leverageEr - b.leverageEr)
      .map((l) => ({
        value: l.leverageEr,
        label: `${l.leverage}X`,
      }));
  },
  ceilLeverageEr(leverageEr, precision, scale) {
    return Big(numUtil.ceil(leverageEr, precision, scale)).times(Big(10).pow(scale)).toNumber();
  },
};

function createInitialMarginEr$(riskLimit$: Observable<RiskLimit>) {
  return riskLimit$.pipe(
    filter(Boolean),
    map((o: RiskLimit) => o.initialMarginEr)
  );
}

function convertMaxLeverage(initialMarginEr: number, ratioScale: number): LeverageEntity {
  const leverageEr = leverageUtil.marginRateErToLeverageEr(initialMarginEr, ratioScale, ratioScale);
  return {
    leverageEr,
    leverageX: leverageUtil.leverageErToLeverage(leverageEr, ratioScale),
  };
}

function convertLeverageOptions(initialMarginEr: number, leverages: Leverage[]) {
  const leverageData = leverages.find((o) => o.initialMarginEr === initialMarginEr);
  return leverageData ? leverageData.options : null;
}

function initMaintenance() {
  return {maintenanceAmountRv: 0, maintenanceMargin: 0, maintenanceMarginRateRr: 0};
}

export function calcMaintenanceMargin(contract, position, leverageMarginsMap) {
  if (!contract) {
    return initMaintenance();
  }

  const {leverageMargin} = contract;
  if (!leverageMarginsMap.has('id_' + leverageMargin)) {
    return initMaintenance();
  }
  if (!position) {
    return initMaintenance();
  }
  const {size, markPriceEp} = position;
  const posValue = calcValueRv(contract.contractSide, contract.contractSize, contract.priceFactor, size, markPriceEp);
  const marginItems = genMarginItems(position, leverageMargin, leverageMarginsMap);
  const item = marginItems
    .sort((a, b) => a.notionalValueRv - b.notionalValueRv)
    .find(({notionalValueRv}) => gte(notionalValueRv, posValue));
  if (!item) {
    return initMaintenance();
  }

  const {maintenanceMarginRateRr, maintenanceAmountRv} = item;
  const maintenanceMargin = Number(Big(posValue).times(maintenanceMarginRateRr).minus(maintenanceAmountRv));
  return {maintenanceAmountRv, maintenanceMargin, maintenanceMarginRateRr: Number(maintenanceMarginRateRr)};
}

// http://confluence.cmexpro.com/pages/viewpage.action?pageId=75268689
function genMarginItems(
  position = {size: 0, riskLimitIndexId: 0},
  leverageMargin,
  marginsMap: Map<string, LeverageMargins>
) {
  const {size, riskLimitIndexId} = position;
  if (size == 0) {
    return marginsMap.get(leverageMargin)?.items || [];
  } else {
    if (riskLimitIndexId == 0) {
      const margin = marginsMap.get(leverageMargin) || {index_id: 0};
      const lastIndexId = Number(margin.index_id) - 1000;
      return (marginsMap.get('id_' + lastIndexId) || marginsMap.get(leverageMargin))?.items || [];
    } else {
      return (marginsMap.get('id_' + riskLimitIndexId) || marginsMap.get(leverageMargin))?.items;
    }
  }
}
